import { useState, useCallback, useRef } from 'react';
import { getAuthHeaders } from '@/lib/api-auth';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface FAQMessage {
  role: 'user' | 'assistant';
  content: string;
  relatedUrl?: string;
  timestamp?: string;
}

// CB-03F: FAQ chat rag_metadata structure
interface FAQRagMetadata {
  faq_search_method: 'hybrid_rrf' | 'simple' | 'none';
  faq_hits_count: number;
  response_time_ms: number;
  query: string;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category?: string;
  related_url?: string;
  keywords: string[];
}

interface FAQSearchResponse {
  items: FAQItem[];
  total: number;
  query: string;
  search_method?: string;
}

export function useFAQChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<FAQMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // CB-03F: Save session to database
  const saveSession = useCallback(
    async (
      currentSessionId: string,
      updatedMessages: FAQMessage[],
      ragMetadata?: FAQRagMetadata
    ) => {
      if (!user) return;

      try {
        // Check if session exists
        const { data: existingSession } = await supabase
          .from('chat_sessions')
          .select('id')
          .eq('id', currentSessionId)
          .single();

        const messagesForDb = updatedMessages.map((m) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp || new Date().toISOString(),
        }));

        if (existingSession) {
          // Update existing session
          await supabase
            .from('chat_sessions')
            .update({
              messages: messagesForDb,
              rag_metadata: ragMetadata || {},
              updated_at: new Date().toISOString(),
            })
            .eq('id', currentSessionId);
        } else {
          // Create new session with session_type: 'faq_chat'
          // P2 fix: Get school_slug from profile for RLS compatibility
          const { data: profile } = await supabase
            .from('profiles')
            .select('school_slug')
            .eq('id', user.id)
            .single();

          const schoolSlug = profile?.school_slug || user.user_metadata?.school_slug;
          if (!schoolSlug) {
            console.warn('[useFAQChat] No school_slug found, skipping session save');
            return;
          }

          await supabase.from('chat_sessions').insert({
            id: currentSessionId,
            user_id: user.id,
            school_slug: schoolSlug,
            session_type: 'faq_chat',
            messages: messagesForDb,
            rag_metadata: ragMetadata || {},
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('[useFAQChat] Failed to save session:', error);
      }
    },
    [user]
  );

  const searchFAQ = async (query: string): Promise<FAQSearchResponse> => {
    const params = new URLSearchParams({
      q: query,
      limit: '3',
    });

    const headers = await getAuthHeaders();
    const response = await fetch(`/api/v1/faq/search?${params}`, { headers });
    if (!response.ok) {
      throw new Error('FAQ search failed');
    }
    return response.json();
  };

  const generateResponse = (items: FAQItem[], query: string): string => {
    if (items.length === 0) {
      return `申し訳ありません。「${query}」に関するFAQが見つかりませんでした。\n\n具体的な内容を運営までお問い合わせください。`;
    }

    // 最も関連性の高いFAQの回答を返す
    const topItem = items[0];
    let response = topItem.answer;

    // 他の関連FAQがあれば追加
    if (items.length > 1) {
      response += '\n\n---\n\n**関連する質問:**\n';
      items.slice(1).forEach((item) => {
        response += `\n• ${item.question}`;
      });
    }

    return response;
  };

  const sendMessage = useCallback(
    async (query: string) => {
      const requestStartTime = Date.now();

      // CB-03F: Ensure session exists
      let currentSessionId = sessionIdRef.current;
      if (!currentSessionId) {
        currentSessionId = crypto.randomUUID();
        sessionIdRef.current = currentSessionId;
        setSessionId(currentSessionId);
      }

      // Add user message with timestamp
      const userMessage: FAQMessage = {
        role: 'user',
        content: query,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        // Search FAQ
        const result = await searchFAQ(query);

        // Generate response
        const responseText = generateResponse(result.items, query);
        const relatedUrl = result.items[0]?.related_url;

        // Add assistant message with timestamp
        const assistantMessage: FAQMessage = {
          role: 'assistant',
          content: responseText,
          relatedUrl,
          timestamp: new Date().toISOString(),
        };

        const updatedMessages = [...messages, userMessage, assistantMessage];
        setMessages((prev) => [...prev, assistantMessage]);

        // CB-03F: Build rag_metadata and save session
        const ragMetadata: FAQRagMetadata = {
          faq_search_method: result.search_method === 'hybrid_rrf' ? 'hybrid_rrf' : 'simple',
          faq_hits_count: result.total,
          response_time_ms: Date.now() - requestStartTime,
          query: query,
        };

        // Save to database (non-blocking)
        saveSession(currentSessionId, updatedMessages, ragMetadata);
      } catch (error) {
        console.error('FAQ chat error:', error);
        const errorMessage: FAQMessage = {
          role: 'assistant',
          content:
            '申し訳ありません。エラーが発生しました。しばらくしてからお試しください。',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMessage]);

        // Save error session
        const updatedMessages = [...messages, userMessage, errorMessage];
        saveSession(currentSessionId, updatedMessages, {
          faq_search_method: 'none',
          faq_hits_count: 0,
          response_time_ms: Date.now() - requestStartTime,
          query: query,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [messages, saveSession]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    // CB-03F: Reset session for new conversation
    sessionIdRef.current = null;
    setSessionId(null);
  }, []);

  // CB-06U: Fetch session history
  const [sessionHistory, setSessionHistory] = useState<Array<{
    id: string;
    created_at: string;
    messages: FAQMessage[];
    preview: string;
  }>>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const fetchSessionHistory = useCallback(async () => {
    if (!user) return;

    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('chat_sessions')
        .select('id, created_at, messages')
        .eq('user_id', user.id)
        .eq('session_type', 'faq_chat')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const history = (data || []).map((session) => {
        const msgs = session.messages as FAQMessage[] || [];
        const firstUserMsg = msgs.find((m) => m.role === 'user');
        return {
          id: session.id,
          created_at: session.created_at,
          messages: msgs,
          preview: firstUserMsg?.content?.substring(0, 30) || '(メッセージなし)',
        };
      });

      setSessionHistory(history);
    } catch (error) {
      console.error('[useFAQChat] Failed to fetch history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user]);

  const loadSession = useCallback((sessionData: {
    id: string;
    messages: FAQMessage[];
  }) => {
    sessionIdRef.current = sessionData.id;
    setSessionId(sessionData.id);
    setMessages(sessionData.messages);
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    clearMessages,
    sessionId,
    // CB-06U: History
    sessionHistory,
    isLoadingHistory,
    fetchSessionHistory,
    loadSession,
  };
}
